import { useQuery } from '@tanstack/react-query';

import { fetchAdminNames } from 'app/hooks/useFetchReconciliationsPage';

import ReconciliationsApiHandler from 'app/services/reconciliationsApiHandler';

import {
  ListPage,
  ReconciliationFinding,
  ReconciliationFindingsRequest,
} from 'app/types/reconciliations';

export const RECONCILIATION_FINDINGS_QUERY_KEY = 'fetchReconciliationFindings';

/**
 * Lists reconciliation findings, such as given ones by id, those a run first found or resolved, every past one never
 * fixed, or every one an admin marked as fixed by hand, naming that admin.
 */
const useFetchReconciliationFindings = (request: ReconciliationFindingsRequest, enabled = true) =>
  useQuery<ListPage<ReconciliationFinding>, Error>(
    [
      RECONCILIATION_FINDINGS_QUERY_KEY,
      request.ids,
      request.firstSeenReconciliationId,
      request.resolvedReconciliationId,
      request.lapsed,
      request.manuallyResolved,
      request.companyIds,
      request.page,
      request.size,
    ],
    async () => {
      const findings = await ReconciliationsApiHandler.listFindings(request);
      const adminIds = [
        ...new Set(findings.list.map((finding) => finding.resolvedBy).filter(Boolean)),
      ] as string[];
      const adminNames = await fetchAdminNames(adminIds);

      return {
        ...findings,
        list: findings.list.map((finding) => ({
          ...finding,
          resolvedByName: finding.resolvedBy ? adminNames.get(finding.resolvedBy) : undefined,
        })),
      };
    },
    {
      enabled:
        enabled &&
        !!(
          request.ids?.length ||
          request.firstSeenReconciliationId ||
          request.resolvedReconciliationId ||
          request.lapsed ||
          request.manuallyResolved
        ),
      keepPreviousData: true,
    }
  );

export default useFetchReconciliationFindings;
